/**
 * Email Integration Tab
 * Main content for the Email tab in Settings page
 */
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Plus, Mail01, AlertCircle } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { EmailAccountsList } from "./email-accounts-list";
import { EmailAccountWizard } from "./email-account-wizard";
import { useEmailAccounts } from "@/hooks/api/use-email-accounts";
import { toast } from "@/lib/toast-service";

export const EmailIntegrationTab = () => {
    const { t } = useTranslation(["pages:email-settings", "common"]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showWizard, setShowWizard] = useState(false);
    const [accountToConfigure, setAccountToConfigure] = useState<string | null>(null);
    const [wizardEmail, setWizardEmail] = useState<string | null>(null);

    const { data: accounts, isLoading, error, refetch } = useEmailAccounts();

    // Handle OAuth callback query params
    useEffect(() => {
        const success = searchParams.get("success");
        const errorParam = searchParams.get("error");
        const message = searchParams.get("message");
        const provider = searchParams.get("provider");

        if (success === "true") {
            toast.success(t("oauth.success", { provider: provider || "Email" }));
            // Refresh accounts list and then open configure modal
            refetch().then(() => {
                // Find the most recently created account (OAuth callback)
                // We'll identify it after refetch completes
                setAccountToConfigure("newest");
            });
        } else if (errorParam === "true") {
            toast.error(message || t("oauth.errorGeneric"));
        }

        // Clean up URL params
        if (success || errorParam) {
            setSearchParams({});
        }
    }, [searchParams, setSearchParams, t, refetch]);

    // Find account to configure after accounts are loaded
    useEffect(() => {
        if (accountToConfigure && accounts && accounts.length > 0) {
            let accountToOpen = null;

            if (accountToConfigure === "newest") {
                // Find the most recently created account (for OAuth callback)
                accountToOpen = accounts.sort((a, b) => {
                    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
                    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
                    return dateB - dateA;
                })[0];
            } else if (accountToConfigure === "byEmail" && wizardEmail) {
                // Find account by email (for IMAP wizard completion)
                accountToOpen = accounts.find(acc => acc.email_address === wizardEmail) || null;
            } else if (accountToConfigure !== "newest" && accountToConfigure !== "byEmail") {
                // Already an account ID, find it
                accountToOpen = accounts.find(acc => acc.account_id === accountToConfigure) || null;
            }

            if (accountToOpen && accountToOpen.account_id !== accountToConfigure) {
                setAccountToConfigure(accountToOpen.account_id);
            }
        }
    }, [accountToConfigure, accounts, wizardEmail]);

    const handleWizardComplete = (email?: string) => {
        setShowWizard(false);
        if (email) {
            setWizardEmail(email);
        }
        refetch().then(() => {
            if (email) {
                // Find account by email after refetch
                setAccountToConfigure("byEmail");
            } else {
                // Find newest account
                setAccountToConfigure("newest");
            }
        });
    };

    const handleWizardCancel = () => {
        setShowWizard(false);
    };

    const handleAddAccount = () => {
        setShowWizard(true);
    };

    // Show error state
    if (error) {
        return (
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="flex items-center gap-3 text-fg-error-primary">
                    <AlertCircle className="h-5 w-5" />
                    <span>{t("errors.loadAccounts")}</span>
                </div>
            </div>
        );
    }

    // Show empty state
    if (!isLoading && (!accounts || accounts.length === 0) && !showWizard) {
        return (
            <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary">
                <div className="flex flex-col items-center justify-center text-center">
                    <div className="mb-4 rounded-full bg-brand-secondary p-4">
                        <Mail01 className="h-8 w-8 text-fg-brand-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-primary mb-2">
                        {t("noAccounts")}
                    </h3>
                    <p className="text-tertiary max-w-md mb-6">
                        {t("noAccountsDescription")}
                    </p>
                    <Button
                        onClick={handleAddAccount}
                        color="primary"
                        size="md"
                        iconLeading={Plus}
                    >
                        {t("addAccount")}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header with Add Button */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-primary">
                        {t("accountsList.title")}
                    </h2>
                </div>
                {!showWizard && (
                    <Button
                        onClick={handleAddAccount}
                        color="primary"
                        size="md"
                        iconLeading={Plus}
                    >
                        {t("addAccount")}
                    </Button>
                )}
            </div>

            {/* Wizard Modal or Account List */}
            {showWizard ? (
                <EmailAccountWizard
                    onComplete={handleWizardComplete}
                    onCancel={handleWizardCancel}
                />
            ) : (
                <EmailAccountsList
                    accounts={accounts || []}
                    isLoading={isLoading}
                    onRefresh={refetch}
                    accountToConfigure={accountToConfigure}
                    onConfigureComplete={() => {
                        setAccountToConfigure(null);
                        setWizardEmail(null);
                    }}
                />
            )}
        </div>
    );
};
